/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { H2, Label, PageActions, PageHeader, PageHeaderContent, PageLabel, TabNav, TabNavItem } from 'components';
// importing this from 'components' is causing a circular dependency
import ConnectedSiteSelector from 'components/SiteSelector/ConnectedSiteSelector';
import { SiteSelectorAppearance } from 'components/SiteSelector/types';
import { theme } from 'design';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { getSiteReviewPayoutsCountRequest } from 'store/Collections/Payouts';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeSite } from 'store/Employers';
import { getSiteBalanceRequest, selectSiteAccountBalance, Site } from 'store/Site';
import { selectReviewPayoutsCount } from 'store/Stats';
import { IStore } from 'store/types';
import { ActivePaymentsTab, PermissionType } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { hasSitePermission } from 'utils/employers';
import { DetailsTab } from './DetailsTab';
import { OverviewTab } from './OverviewTab';
import ReviewPayments from './ReviewPayments';

const PaymentsScreen = ({ activeTab }: { activeTab: ActivePaymentsTab }) => {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentEmployeeOrg);
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const history = useHistory();
  const reviewTotal = useSelector(selectReviewPayoutsCount()) || 0;
  const reviewPermission = hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_APPROVAL);
  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId;

  useEffect(() => {
    if (currentSite) dispatch(getSiteBalanceRequest(currentSite.id));
  }, [dispatch, currentSite]);

  useEffect(() => {
    if (currentSiteId && reviewPermission) {
      dispatch(getSiteReviewPayoutsCountRequest(currentSiteId));
    }
  }, [dispatch, currentSiteId, reviewPermission]);

  useEffect(() => {
    if (activeTab === ActivePaymentsTab.REVIEW && (!currentSiteId || (currentSite && !reviewPermission))) {
      history.push(routeConstants.private.paymentsOverview);
    }
  }, [history, activeTab, currentSiteId, currentSite, reviewPermission]);

  const accountBalance = useSelector(selectSiteAccountBalance());
  const isAccountBalanceLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.GET_SITE_BALANCE_REQUEST));
  const activeBalance = { value: accountBalance, label: 'Account' };

  return (
    <>
      <PageHeader>
        <PageLabel>Payments</PageLabel>

        <PageHeaderContent>
          <ConnectedSiteSelector appearance={SiteSelectorAppearance.PAGE_HEADER} />
          <PageActions display="column" align="end">
            <Label>{activeBalance.label} Balance</Label>
            <H2 data-cy="subtitle" color={activeBalance && activeBalance.value < 0 ? theme.color.danger[50] : undefined}>
              {isAccountBalanceLoading ? <Skeleton width={275} /> : activeBalance.value !== undefined ? centsToDollarsDisplay(activeBalance.value) : null}
            </H2>
          </PageActions>
        </PageHeaderContent>

        <TabNav>
          <TabNavItem data-cy="overview-nav-btn" label="Overview" isActive={activeTab === ActivePaymentsTab.OVERVIEW} to={routeConstants.private.paymentsOverview} />
          <TabNavItem data-cy="details-nav-btn" label="Details" isActive={activeTab === ActivePaymentsTab.DETAILS} to={routeConstants.private.paymentsDetail} />
          {reviewPermission && <TabNavItem data-cy="review-nav-btn" label="Review" isActive={activeTab === ActivePaymentsTab.REVIEW} to={routeConstants.private.paymentsReview} isUpdated={reviewTotal > 0} />}
        </TabNav>
      </PageHeader>

      {activeTab === ActivePaymentsTab.OVERVIEW && currentOrganization && currentSite && <OverviewTab />}
      {activeTab === ActivePaymentsTab.DETAILS && currentOrganization && currentSite && <DetailsTab />}
      {activeTab === ActivePaymentsTab.REVIEW && currentOrganization && currentSite && <ReviewPayments />}
    </>
  );
};

export default PaymentsScreen;
